import {
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react";
import { insurerShort } from "../../../constants/insurers.ts";
import InsuranceService from "../../../services/InsuranceService/InsuranceService.ts";
import { LifecycleService } from "../../../services/Integrations/LifecycleService.ts";
import PolicyService from "../../../services/PolicyService/PolicyService.ts";
import type { PolicyValuesObject } from "../../../types/insurance.ts";
import type {
  CanonicalPolicy,
  LifecycleOperation,
  LocalPaymentMethod,
  ProviderId,
  Schedule,
} from "../../../types/lifecycle.ts";
import {
  installmentKey,
  isPayable,
  LifecycleOperations,
} from "../Lifecycle/operations.ts";
import {
  paymentBlanks,
  useBlankInventory,
  validPaymentBlanks,
} from "../Lifecycle/useBlankInventory.ts";
import { providerBaseIds } from "../Lifecycle/useBooking.ts";
import { useLookup } from "../Lifecycle/useLookup.ts";

/** VALIORS.DIV_ID 0 is unpaid and -2 is "Чакащ" (awaiting payment). */
export const isLocallyUnpaid = (row: PolicyValuesObject): boolean =>
  ["", "0", "-2"].includes(String(row.paymentLocation ?? "").trim());

interface LoadedPolicy {
  policy: CanonicalPolicy;
  schedule: Schedule;
  localPolicyId: number;
  /** The local INS_COMP.CID; blanks are registered per insurer agency. */
  localCid: string;
  /** The insurer agency's name in Wing. */
  agencyName: string;
  /** The policy's local AGENTS.AID. */
  localAgentId: string;
  installments: PolicyValuesObject[];
}

const directory = (value: unknown): Record<string, string> =>
  value && typeof value === "object" ? (value as Record<string, string>) : {};

export function useInstallmentPayment(
  jwt: string,
  headOff: string,
  provider: ProviderId,
  initialPolicyNumber = ""
) {
  const operations = useMemo(() => new LifecycleOperations(), []);
  const state = useSyncExternalStore(
    operations.subscribe,
    operations.getSnapshot
  );
  const [policyNumber, setPolicyNumber] = useState(initialPolicyNumber);
  const [loaded, setLoaded] = useState<LoadedPolicy | null>(null);
  const [searching, setSearching] = useState(false);
  const [searchError, setSearchError] = useState<unknown>(null);
  const [refreshFailed, setRefreshFailed] = useState(false);
  const [paymentMethod, setPaymentMethod] =
    useState<LocalPaymentMethod>("cash");
  const [officeChoice, setOfficeChoice] = useState("");
  const [agentChoice, setAgentChoice] = useState("");
  const [printing, setPrinting] = useState(false);
  const [printError, setPrintError] = useState<unknown>(null);
  // Counts searches. Searches and post-payment refreshes overlap; only the latest
  // search may set the loaded policy, or a late refresh of policy A would replace
  // a search for B.
  const loadSequence = useRef(0);
  const currentLoaded = useRef(loaded);
  currentLoaded.current = loaded;
  // Payments recorded before the last search belong to what was shown then.
  const [paymentsBeforeSearch, setPaymentsBeforeSearch] = useState(0);
  // The last ?policy= link searched; a remount would drop a pending retry.
  const linkedPolicy = useRef("");

  const capabilities = useLookup(jwt ? `${jwt}|capabilities` : "", () =>
    LifecycleService.getCapabilities(jwt)
  );
  const officeLookup = useLookup(jwt ? `${jwt}|offices` : "", async () =>
    directory(await InsuranceService.getAllOffices(jwt, false))
  );
  const offices = officeLookup.value ?? {};
  const officeId =
    officeChoice || (offices[headOff] ? String(headOff) : "");
  const agentLookup = useLookup(jwt ? `${jwt}|agents` : "", async () =>
    directory(await InsuranceService.getAllAgents(jwt))
  );
  const agents = agentLookup.value ?? {};

  // The insurer applies a payment to its next due installment, and Wing marks
  // its earliest unpaid one. Both must exist and agree on how many remain.
  const nextRow = loaded?.schedule.installments.find(isPayable) ?? null;
  const unpaidLocal = (loaded?.installments ?? [])
    .filter(isLocallyUnpaid)
    .sort((a, b) => Number(a.installmentSequence) - Number(b.installmentSequence));
  const localNext = unpaidLocal[0] ?? null;

  // The new blanks come from the agent of the installment being paid, unless
  // the operator picks another agent of the policy's insurer agency.
  const defaultAgentId =
    [localNext?.agentId, loaded?.localAgentId]
      .map((value) => String(value ?? ""))
      .find((value) => agents[value]) ?? "";
  const agentId = agents[agentChoice] ? agentChoice : defaultAgentId;
  const blankForm = useBlankInventory(
    jwt,
    provider,
    state.inventoryRevision,
    true,
    loaded?.localCid ?? "",
    agentId
  );
  const capability = capabilities.value?.find(
    (entry) => entry.provider === provider
  );
  const can = (operation: LifecycleOperation) =>
    capability?.operations[operation]?.state === "supported";

  const load = async (number: string): Promise<LoadedPolicy> => {
    const [found, local] = await Promise.all([
      LifecycleService.getSchedule(jwt, number),
      PolicyService.fetchPolicyByPolicyNumber(number, jwt),
    ]);
    if (found.policy.provider !== provider)
      throw new Error(
        `Полица № ${number} е издадена от ${insurerShort(
          found.policy.provider
        )}.`
      );
    const localPolicyId = Number(local.policyId);
    if (!localPolicyId)
      throw new Error(`Полица № ${number} не е намерена в системата.`);
    const companies = directory(
      await InsuranceService.getAllInsuranceCompanies(
        jwt,
        false,
        providerBaseIds[provider]
      )
    );
    const localCid = String(local.insuranceCompanyId);
    if (!companies[localCid])
      throw new Error(
        `Полица № ${number} е записана към друг застраховател.`
      );
    const installments = await PolicyService.fetchValuesByPolicyId(
      String(localPolicyId),
      jwt
    );
    return {
      policy: found.policy,
      schedule: found.schedule,
      localPolicyId,
      localCid,
      agencyName: companies[localCid],
      localAgentId: String(local.agentId ?? ""),
      installments,
    };
  };

  // A newer search replaces one still running. None starts during a payment,
  // whose refresh must land on the policy it paid.
  const search = async (requested?: string) => {
    const number = (requested ?? policyNumber).trim();
    if (!jwt || !number || state.busy) return;
    const sequence = ++loadSequence.current;
    if (requested !== undefined) setPolicyNumber(requested);
    setSearching(true);
    setSearchError(null);
    setRefreshFailed(false);
    setLoaded(null);
    setAgentChoice("");
    setPrintError(null);
    setPaymentsBeforeSearch(operations.getSnapshot().payments.length);
    operations.clearError();
    try {
      const result = await load(number);
      if (sequence === loadSequence.current) setLoaded(result);
    } catch (error) {
      if (sequence === loadSequence.current) setSearchError(error);
    } finally {
      if (sequence === loadSequence.current) setSearching(false);
    }
  };

  // A link opens the page with ?policy= filled in. A later link searches again
  // in place, once a running payment has finished.
  useEffect(() => {
    if (!initialPolicyNumber) {
      linkedPolicy.current = "";
      return;
    }
    if (state.busy || linkedPolicy.current === initialPolicyNumber) return;
    linkedPolicy.current = initialPolicyNumber;
    void search(initialPolicyNumber);
  }, [initialPolicyNumber, state.busy]);

  const insurerDue = new Set(
    (loaded?.schedule.installments ?? [])
      .filter((row) => row.status === "due")
      .map((row, index) => row.dueDate ?? `row-${index}`)
  ).size;
  const mismatch = Boolean(
    loaded &&
      loaded.schedule.completeness !== "projection" &&
      insurerDue !== unpaidLocal.length
  );
  const nextKey =
    loaded && nextRow ? installmentKey(loaded.policy, nextRow) : "";
  const paidNow = state.payments.find((payment) => payment.key === nextKey);

  const canPay = Boolean(
    loaded &&
      nextRow &&
      localNext &&
      !mismatch &&
      !paidNow &&
      can("installments.payment") &&
      !state.busy &&
      !state.localWriteRetry &&
      !blankForm.loading &&
      !blankForm.error &&
      validPaymentBlanks(provider, blankForm.blanks) &&
      (paymentMethod !== "cash" || Boolean(offices[officeId]))
  );

  // A refresh after a payment lands only if no search started since `sequence`
  // was taken, before the payment. It never supersedes a search itself.
  const reload = async (policy: CanonicalPolicy, sequence: number) => {
    if (sequence !== loadSequence.current) return;
    try {
      const result = await load(policy.policyNumber);
      if (sequence !== loadSequence.current) return;
      setLoaded(result);
      setRefreshFailed(false);
    } catch {
      if (sequence === loadSequence.current) setRefreshFailed(true);
    }
  };

  const pay = async () => {
    if (!canPay || !loaded || !nextRow || !localNext) return;
    const sequence = loadSequence.current;
    await operations.pay(jwt, loaded.policy, nextRow, {
      policyNumber: loaded.policy.policyNumber,
      paymentTarget: nextRow.paymentTarget,
      amount: { amount: nextRow.amount.amount, currency: nextRow.amount.currency },
      // The broker collects the money, so the insurer is paid at its cash desk.
      // How the client paid (cash, POS or bank) is recorded locally.
      paymentMethod: "cash",
      blanks: paymentBlanks(provider, blankForm.blanks),
      localWrite: {
        policyId: loaded.localPolicyId,
        installment: Number(localNext.installmentSequence),
        paymentMethod,
        ...(paymentMethod === "cash" ? { divisionId: Number(officeId) } : {}),
      },
    });
    if (operations.getSnapshot().payments.some((p) => p.key === nextKey))
      await reload(loaded.policy, sequence);
  };

  // Recovery works whichever policy is loaded: the accepted payment must be
  // recorded. The schedule is refreshed when the retried policy is the one shown.
  const retryLocalWrite = async () => {
    const retried = state.localWriteRetry?.request.policyNumber;
    const sequence = loadSequence.current;
    await operations.retryLocalWrite(jwt);
    const shown = currentLoaded.current;
    if (
      !operations.getSnapshot().localWriteRetry &&
      shown &&
      shown.policy.policyNumber === retried
    )
      await reload(shown.policy, sequence);
  };

  // The latest payment of the loaded policy, or one recorded since the last
  // search (a retry for another policy). OZK prints its electronic receipt, with
  // the green card the payment issued, from the receiptRef it returned.
  const lastPayment =
    state.payments
      .filter(
        (payment, index) =>
          index >= paymentsBeforeSearch ||
          payment.response.policy.policyNumber === loaded?.policy.policyNumber
      )
      .at(-1) ?? null;
  const canPrintReceipt =
    provider === "ozk" && Boolean(lastPayment?.response.receiptRef);
  const printReceipt = async () => {
    const receiptRef = lastPayment?.response.receiptRef;
    if (!lastPayment || !receiptRef || printing) return;
    setPrinting(true);
    setPrintError(null);
    try {
      const blob = await LifecycleService.getReceiptDocument(jwt, receiptRef);
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      const installment = lastPayment.response.localWrite?.installment;
      link.href = url;
      link.download = `${lastPayment.response.policy.policyNumber.replace(
        /\//g,
        "-"
      )}${installment ? `-vnoska-${installment}` : ""}.pdf`;
      link.click();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (error) {
      setPrintError(error);
    } finally {
      setPrinting(false);
    }
  };

  return {
    policyNumber,
    setPolicyNumber,
    search,
    searching,
    searchError,
    loaded,
    nextRow,
    localNext,
    mismatch,
    insurerDue,
    localUnpaid: unpaidLocal.length,
    capability,
    capabilitiesLoading: capabilities.loading,
    can,
    canPay,
    pay,
    retryLocalWrite,
    refreshFailed,
    operationState: state,
    lastPayment,
    canPrintReceipt,
    printReceipt,
    printing,
    printError,
    blankForm,
    paymentMethod,
    setPaymentMethod,
    offices,
    officeId,
    setOfficeId: setOfficeChoice,
    agents,
    agentId,
    setAgentId: setAgentChoice,
  };
}

export type InstallmentPaymentController = ReturnType<
  typeof useInstallmentPayment
>;
